<template>
  <v-container fluid>
    <avs-backButton
      @click.stop="$store.commit('app/setProject', project)"
      backTo="Project"
    />

    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.description]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.description">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep
          step="2"
          :rules="[() => isFormValid.steps]"
        >
          {{ $t("categories.project.act_task") }}
          <small v-show="!isFormValid.steps">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="3">{{ $t("views.team.title") }}</AvsStep>
      </template>

      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-description"
            v-model="isFormValid.description"
            class="mt-5"
          >
            <v-text-field
              v-model="project.name"
              :rules="rules.name"
              counter="255"
              maxlength="255"
              clearable
              dense
              outlined
              :label="$t('commons.name')"
              type="text"
            />

            <v-textarea
              v-model="project.description"
              :rules="rules.description"
              clearable
              dense
              outlined
              :label="$t('commons.description')"
              rows="4"
            />

            <DateInput
              v-model="project.start_date"
              :rules="rules.start_date"
              :label="$t('commons.date.start_date')"
              :max="toISODate(project.end_date)"
              dense
            ></DateInput>

            <DateInput
              v-model="project.end_date"
              :rules="rules.end_date"
              :label="$t('commons.date.end_date')"
              :min="toISODate(project.start_date)"
              dense
            ></DateInput>

            <CompanyInput
              v-model="$store.getters['app/getCompany']"
              disabled
            />

            <DepartmentInput
              v-model="project.department"
              :rules="rules.department"
            />

            <v-col class="col-12 pb-0 mb-0">
              <v-checkbox v-model="project.show_pcq_dsi">
                <template v-slot:label>
                  <div>{{ $t("categories.pcqdsi.show_pcq_dsi") }}</div>
                </template>
              </v-checkbox>
            </v-col>

            <ImageInput
              v-model="project.picture"
              dense
            />
            <ImageInput
              v-model="project.logo"
              dense
              :spanName="'Logo'"
            />
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="2">
          <ProjectActivityList
            ref="form-steps"
            v-model="isFormValid.steps"
            :project="project"
            :pcq_dsi="project.show_pcq_dsi"
            @updated="v => getProject(project.id)"
          />
        </v-stepper-content>

        <v-stepper-content step="3">
          <ProjectTeam v-if="isProjectCreated" />
        </v-stepper-content>
      </template>

      <template v-slot:actions>
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-left"
          @click="tab--"
          :disabled="tab <= 1"
          :name="$t('commons.previous')"
        />
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-right"
          right
          @click="tabIncrement"
          :name="nextLabel"
        />
      </template>
    </AvsSteppers>
  </v-container>
</template>

<script>
import _ from "lodash"
import { ProjectService, StepService, TaskService, InspectionService } from "@/services"
import { CompanyInput, DateInput, ImageInput } from "@/components"
import ProjectTeam from "@/views/project/ProjectTeam.vue"
// import { NEXT } from "@/utils/consts"
import { mkProject } from "@/utils/datamodels"
import { required } from "@/utils/validator"
import { toISODate } from "@/utils/datetime"

export default {
  name: "ProjectCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    CompanyInput,
    ImageInput,
    DateInput,
    ProjectActivityList: () => import("@/components/ri3/project/ProjectActivityList.vue"),
    ProjectTeam,
    DepartmentInput: () => import("@/components/inputs/DepartmentInput.vue")
  },
  data: function () {
    const company = this.$store.getters["app/getCompany"]

    return {
      projectRef: null,
      project: mkProject(company, this.$t("commons.project_name")),
      tab: this.$route.params.tab || 1,
      loading: false,
      rules: {
        name: [required],
        description: [],
        start_date: [required],
        end_date: [required],
        step: {
          name: [required],
          start_date: [required],
          deadline: [required]
        },
        task: {
          name: [required],
          status: [required],
          start_date: [required],
          deadline: [required]
        },
        department: []
      },
      isFormValid: {
        description: false,
        steps: true
      },

      nextLabel: this.$t("commons.next"), //NEXT.DEFAULT,
      isProjectCreated: false,
      showInput: false
    }
  },
  methods: {
    toISODate,
    notify(msg, color = "success") {
      this.$store.commit("app/message", { msg, color })
    },
    prepareProjectForSubmission(project) {
      return {
        ...project,
        department: project.department ? project.department.id : null
      }
    },
    async saveProject() {
      this.project.lang = this.$i18n.locale
      this.project.department = this.project.department ? this.project.department.id : null
      const isFormStepsValid = this.$refs["form-steps"].validate()
      const isFormDescriptionValid = this.$refs["form-description"].validate()
      if (isFormStepsValid && isFormDescriptionValid) {
        const saveMethod = this.isProjectCreated ? this.updateProject : this.createProject
        // const preparedProject = this.prepareProjectForSubmission(this.project)
        return saveMethod()
          .then(savedId => {
            this.getProject(savedId)
          })
          .catch(error => {
            // API saving error redirect to tab 1
            this.tab = 1
          })
      } else {
        // Invalid form error redirect to tab 2
        this.notify(this.$t("messages.warning.all_required_info"), "warning")
        this.tab = 2

        const msg = `IsFormStepsValid: ${isFormStepsValid}, IsFormDescriptionValid: ${isFormDescriptionValid}`
        return Promise.reject(msg)
      }
    },
    async getProject(projectId) {
      return ProjectService.get(this, null, projectId).then(r => {
        this.project = {
          ...r,
          // Order steps by start_date ascend
          steps: r.steps.sort((a, b) => a.start_date > b.start_date)
        }
        this.projectRef = _.cloneDeep(this.project)
        this.storeProject()
        this.isProjectCreated = true
      })
    },
    tabIncrement() {
      return this.tab < 3 ? this.tab++ : this.$router.push({ name: "Project" })
    },
    async createProject() {
      this.loading = true
      return ProjectService.create(this, this.project)
        .then(response => {
          this.notify(
            this.$t("messages.success.created", { entity: this.$tc("categories.project.name", 1) })
          )
          return response.id
        })
        .finally(() => {
          this.loading = false
        })
    },
    async updateProject() {
      return new Promise((resolve, reject) => {
        let requests = []
        requests = requests.concat(this.handleStepsUpdate(this.project.steps))
        const projectAttrs = [
          "name",
          "description",
          "company",
          "department",
          "start_date",
          "end_date",
          "picture",
          "show_pcq_dsi",
          "logo"
        ]
        const pickProject = _.pick(this.project, projectAttrs)
        const pickProjectRef = _.pick(this.projectRef, projectAttrs)
        if (!_.isEqual(pickProjectRef, pickProject)) {
          requests.push(ProjectService.update(this, this.project.id, pickProject))
        }

        if (requests.length > 0) {
          this.loading = true
          Promise.all(requests)
            .then(() => {
              this.notify(
                this.$t("messages.success.updated", {
                  entity: this.$tc("categories.project.name", 1)
                })
              )
            })
            .finally(() => {
              this.loading = false
              resolve(this.project.id)
            })
        } else {
          this.notify(this.$t("messages.success.no_updated"), "info")
          resolve(this.project.id)
        }
      })
    },
    handleStepTasksUpdate(step, stepRef) {
      const requests = []
      const taskAttrs = ["create_pcq_dsi", "name", "order", "start_date", "deadline", "status"]

      step.tasks.map(t => {
        if (t.id) {
          //  Delete
          const taskRef = stepRef.tasks.find(tRef => tRef.id === t.id)
          if (!_.isEqual(_.pick(t, taskAttrs), _.pick(taskRef, taskAttrs))) {
            requests.push(
              TaskService.update(this, t.id, {
                ..._.pick(t, taskAttrs),
                step: stepRef.id
              })
            )
          }
        } else {
          //  Create
          requests.push(
            TaskService.create(this, {
              ..._.pick(t, taskAttrs),
              step: stepRef.id,
              validation_needed: false,
              delay: null
            })
          )
        }
      })

      return requests
    },
    handleStepInspectionsUpdate(step, stepRef) {
      const requests = []
      const inspectionAttrs = ["name", "order", "start_date", "end_date", "status"]

      step.inspections.map(t => {
        if (t.id) {
          //  Delete
          const inspectionRef = stepRef.inspections.find(tRef => tRef.id === t.id)
          if (!_.isEqual(_.pick(t, inspectionAttrs), _.pick(inspectionRef, inspectionAttrs))) {
            requests.push(
              InspectionService.update(this, t.id, {
                ..._.pick(t, inspectionAttrs),
                step: stepRef.id
              })
            )
          }
        } else {
          //  Create
          requests.push(
            InspectionService.create(this, this.project.id, {
              ..._.pick(t, inspectionAttrs),
              step: stepRef.id,
              validation_needed: false,
              delay: null
            })
          )
        }
      })

      return requests
    },
    handleStepsUpdate(steps) {
      let requests = []

      const stepAttrs = ["name", "start_date", "deadline", "order"]
      steps.map(s => {
        if (!s.id) {
          requests.push(StepService.create(this, this.project.id, s))
        } else {
          let stepRef = this.projectRef.steps.find(sRef => sRef.id === s.id)
          requests = requests
            .concat(this.handleStepTasksUpdate(s, stepRef))
            .concat(this.handleStepInspectionsUpdate(s, stepRef))
          if (stepRef && !_.isEqual(_.pick(s, stepAttrs), _.pick(stepRef, stepAttrs))) {
            let updateStep = _.pick(s, stepAttrs)
            requests.push(StepService.update(this, s.id, updateStep))
          }
        }
      })
      this.projectRef.steps.map(s => {
        let stepRef = steps.find(sRef => sRef.id === s.id)
        if (!stepRef) requests.push(StepService.remove(this, s.id))
      })
      return requests
    },
    storeProject() {
      this.$store.commit("app/setProject", this.project)
    }
  },
  created() {
    if (this.projectId) this.getProject(this.projectId)
  },
  watch: {
    tab(newValue, oldValue) {
      // Set Save label
      if (newValue === 2) {
        this.nextLabel = this.$t("commons.save_next") //NEXT.SAVE
      } else if (newValue === 3) {
        this.nextLabel = this.$t("commons.finish") //NEXT.FINISH
      } else {
        this.nextLabel = this.$t("commons.next") // NEXT.DEFAULT
      }

      if (oldValue === 1) {
        this.tab = this.$refs["form-description"].validate() ? 2 : 1
      } else if (oldValue === 2 && newValue === 3) {
        this.saveProject()
      }
    }
  },
  computed: {
    projectId() {
      return this.$route.params.id || this.project.id || null
    },
    title() {
      return this.projectId
        ? this.$t("actions.edit", { entity: this.$tc("categories.project.name", 1).toLowerCase() })
        : this.$t("actions.initiate", {
            entity: this.$tc("categories.project.name", 1).toLowerCase()
          })
    }
  },
  mounted() {
    if (this.$route.query?.chosenTab) {
      this.tab = this.$route.query.chosenTab
    }
  }
}
</script>

<style scoped>
.activity-title {
  border-left: 5px solid #452078;
}
.item-container {
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  margin-bottom: 16px;
}
.task-title {
  border-left: 5px solid #a4c11f;
}
</style>
